import React from 'react';

import { darken, transparentize } from 'polished';
import styled from 'styled-components';

import { COLOR, FONT_SIZE, RADIUS, SPACING } from '../../packages/constants';

export interface AutoCompleteTextInputProps {
    name: string;
    textOnChange: (value: string) => void;
    dropdownOnChange: (value: string, command: DropDownAction) => void;
    defaultItems?: string[];
    textValue?: string;
    defaultPlaceholder?: string;
    ariaLabel?: string;
    dataId?: string;
    fontSize?: number;
    lineHeight?: number;
    invalid?: boolean;
    disabled?: boolean;
    required?: boolean;
}

export enum DropDownAction {
    Insert = 'INSERT',
    Remove = 'REMOVE',
}

interface KeyboardEvent {
    key: string;
}

interface TextBoxProps {
    invalid?: boolean;
    disabled?: boolean;
    name?: string;
    fontSize?: number;
    lineHeight?: number;
}

const TextBox = styled.input<TextBoxProps>`
    background-color: ${(props: TextBoxProps): string =>
        props.disabled ? darken('0.1', COLOR.PRIMARY.WHITE) : COLOR.PRIMARY.WHITE};
    color: ${(props: TextBoxProps): string =>
        props.disabled ? transparentize('0.5', COLOR.PRIMARY.BLACK) : COLOR.PRIMARY.BLACK};
    font-size: ${(props: TextBoxProps): string => (props.fontSize ? `${props.fontSize}px` : FONT_SIZE.SMALL)};
    line-height: ${(props: TextBoxProps): string => (props.lineHeight ? `${props.lineHeight}px` : FONT_SIZE.MEDIUM)};
    padding: ${SPACING.SMALL};
    border-width: 1px;
    border-style: solid;
    border-color: ${(props: TextBoxProps): string => (props.invalid ? COLOR.PRIMARY.RED : COLOR.MAIN.FORM_FIELD)};
    border-radius: ${RADIUS.DEFAULT};

    &::placeholder {
        opacity: 0.65;
    }
`;

const WrapperRef = styled.div`
    display: flex;
    flex-direction: row;
`;

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;

    button {
        padding: 7px 20px;
        margin: 0 auto auto 10px;
    }
`;

const OptionWrapper = styled.div`
    position: relative;
`;

const ControlWrapper = styled.div`
    position: absolute;
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const StyledOption = styled.span<TextBoxProps>`
    font-size: ${(props: TextBoxProps): string => (props.fontSize ? `${props.fontSize}px` : FONT_SIZE.SMALL)};
    line-height: ${(props: TextBoxProps): string => (props.lineHeight ? `${props.lineHeight}px` : FONT_SIZE.MEDIUM)};
    background-color: ${(props: TextBoxProps): string =>
        props.disabled ? darken('0.1', COLOR.PRIMARY.WHITE) : COLOR.PRIMARY.WHITE};
    color: ${(props: TextBoxProps): string =>
        props.disabled ? transparentize('0.5', COLOR.PRIMARY.BLACK) : COLOR.PRIMARY.BLACK};
    padding: ${SPACING.SMALL};
    border-color: ${(props: TextBoxProps): string => (props.invalid ? COLOR.PRIMARY.RED : COLOR.MAIN.FORM_FIELD)};
    border-radius: ${RADIUS.DEFAULT};
    border-left: 1px solid
        ${(props: TextBoxProps): string => (props.invalid ? COLOR.PRIMARY.RED : COLOR.MAIN.FORM_FIELD)};
    border-right: 1px solid
        ${(props: TextBoxProps): string => (props.invalid ? COLOR.PRIMARY.RED : COLOR.MAIN.FORM_FIELD)};

    &:last-of-type {
        border-bottom: 1px solid
            ${(props: TextBoxProps): string => (props.invalid ? COLOR.PRIMARY.RED : COLOR.MAIN.FORM_FIELD)};
    }
    &:hover,
    &:focus {
        background-color: ${COLOR.DROPDOWN.DROPDOWN_OPTION_HOVER};
        outline: none;
    }
`;

export const AutoCompleteTextInput = React.forwardRef<HTMLInputElement, AutoCompleteTextInputProps>(
    (
        {
            defaultItems = [],
            defaultPlaceholder = 'Please enter a value',
            ariaLabel = 'Autocomplete input',
            textValue = '',
            textOnChange,
            dropdownOnChange,
            dataId,
            disabled,
            invalid,
            name,
            required,
            fontSize,
            lineHeight,
        },
        ref,
    ) => {
        const [displayDropdownList, setDisplayDropdownList] = React.useState(false);
        const [placeholder, setPlaceholder] = React.useState(defaultPlaceholder);
        const [selectedIndex, setSelectedIndex] = React.useState(-1);
        const wrapperRef = React.useRef<HTMLDivElement>(null);
        const selectRef = React.useRef<HTMLDivElement>(null);

        const maxListLength = 2;
        const filteredList = defaultItems.filter((item: string) =>
            textValue ? item.toLowerCase().includes(textValue.toLowerCase()) : true,
        );
        const limitedFilteredList = filteredList.slice(0, 3);

        React.useEffect(() => {
            document.addEventListener('mousedown', hideList);

            return () => {
                document.removeEventListener('mousedown', hideList);
            };
        });

        React.useEffect(() => {
            if (!selectRef.current) return;

            selectRef.current.focus();
        }, [selectedIndex]);

        const removeWhiteSpace = (id: string) => {
            return id.trim();
        };

        const hideList = (e: { target: any }) => {
            const { current: wrap } = wrapperRef;
            if (wrap && !wrap.contains(e.target)) {
                setDisplayDropdownList(false);
                setPlaceholder(defaultPlaceholder);
                setSelectedIndex(-1);
            }
        };

        const search = (value: string) => {
            textOnChange(value);
            setDisplayDropdownList(true);
        };

        const selectNext = (e: any) => {
            e.preventDefault();
            const isLastOrUnselected =
                selectedIndex === limitedFilteredList.length - 1 ||
                selectedIndex === maxListLength ||
                selectedIndex === -1;
            const newIndex = isLastOrUnselected ? 0 : selectedIndex + 1;
            setSelectedIndex(newIndex);
        };

        const selectPrevious = (e: any) => {
            e.preventDefault();
            const isFirstOrUnselected = selectedIndex === 0 || selectedIndex === -1;
            const lastItemIndex = Math.min(limitedFilteredList.length - 1, maxListLength); // Not always showing all 3 items
            const newIndex = isFirstOrUnselected ? lastItemIndex : selectedIndex - 1;
            setSelectedIndex(newIndex);
        };

        const appendValue = (value: string) => {
            value = removeWhiteSpace(value);
            const getId = defaultItems.indexOf(value);
            if (getId !== -1) {
                // element exists, we need to remove it and move it to the start of the array
                defaultItems.splice(getId, 1);
            }
            if (value !== '' && value !== defaultPlaceholder) {
                dropdownOnChange(value, DropDownAction.Insert);
            }
        };

        const dropdownListKeyHandler = (event: any, item: string) => {
            const itemIndex = defaultItems.indexOf(item);

            if (event.shiftKey && event.key === 'Delete') {
                if (itemIndex !== -1) {
                    dropdownOnChange(defaultItems[itemIndex], DropDownAction.Remove);
                    setPlaceholder(limitedFilteredList[selectedIndex]);
                    if (filteredList.length - 1 <= selectedIndex) {
                        setSelectedIndex(selectedIndex - 1);
                    }
                    // If the array only one has item, and we just removed it
                    if (defaultItems.length === 0 && itemIndex !== -1) {
                        setPlaceholder(defaultPlaceholder);
                        setSelectedIndex(-1); // List is empty
                    }
                }
            }
        };

        const wrapperKeyHandler = (event: KeyboardEvent) => {
            switch (event.key) {
                case 'ArrowUp':
                    selectPrevious(event);
                    break;

                case 'ArrowDown':
                    selectNext(event);
                    break;

                case 'Enter':
                    displayDropdownList
                        ? selectRef.current
                            ? selectValue(placeholder)
                            : appendValue(textValue)
                        : setDisplayDropdownList(true);
                    displayDropdownList ? (placeholder == defaultPlaceholder ? setDisplayDropdownList(false) : '') : '';
                    break;

                case 'Tab':
                    displayDropdownList ? '' : setDisplayDropdownList(true);
                    break;

                case 'Escape':
                    displayDropdownList ? (setDisplayDropdownList(false), setPlaceholder(defaultPlaceholder)) : '';
                    break;
            }
        };

        const selectValue = (value: string) => {
            if (value !== defaultPlaceholder) {
                textOnChange(removeWhiteSpace(value));
            }
            setDisplayDropdownList(false);
            setPlaceholder(defaultPlaceholder);
            appendValue(value);
            setSelectedIndex(-1);
        };

        return (
            <WrapperRef ref={ref} data-test-id={`${dataId}`}>
                <Wrapper ref={wrapperRef} onKeyDown={wrapperKeyHandler}>
                    <TextBox
                        placeholder={placeholder}
                        onClick={() => setDisplayDropdownList(defaultItems.length !== 0 && !displayDropdownList)}
                        onChange={(e: { target: { value: any } }) => search(e.target.value)}
                        value={textValue}
                        autoComplete={'off'}
                        aria-label={ariaLabel}
                        fontSize={fontSize}
                        lineHeight={lineHeight}
                        disabled={disabled}
                        invalid={invalid}
                        name={name}
                        required={required}
                    />
                    <OptionWrapper>
                        <ControlWrapper>
                            {displayDropdownList
                                ? limitedFilteredList.map((item: string, index: any) => (
                                      <StyledOption
                                          ref={index === selectedIndex ? selectRef : () => ''}
                                          key={index}
                                          data-index={index}
                                          onClick={() => {
                                              !disabled ? selectValue(item) : setPlaceholder(defaultPlaceholder);
                                          }}
                                          onMouseOver={() => {
                                              !disabled
                                                  ? (setSelectedIndex(index), setPlaceholder(removeWhiteSpace(item)))
                                                  : setPlaceholder(defaultPlaceholder);
                                          }}
                                          onMouseOut={() => {
                                              setPlaceholder(defaultPlaceholder);
                                              setSelectedIndex(-1);
                                          }}
                                          onKeyDown={(e: any) => dropdownListKeyHandler(e, item)}
                                          tabIndex={0}
                                          onFocus={() => {
                                              !disabled
                                                  ? (setSelectedIndex(index), setPlaceholder(removeWhiteSpace(item)))
                                                  : setPlaceholder(defaultPlaceholder);
                                          }}
                                          disabled={disabled}
                                          invalid={invalid}
                                          name={name}
                                          fontSize={fontSize}
                                          lineHeight={lineHeight}
                                      >
                                          {removeWhiteSpace(item)}
                                      </StyledOption>
                                  ))
                                : ''}
                        </ControlWrapper>
                    </OptionWrapper>
                </Wrapper>
            </WrapperRef>
        );
    },
);

AutoCompleteTextInput.displayName = 'AutoCompleteTextInput';
